/**
 * Page d'administration complète pour la gestion des Payins (commissions dues par les propriétaires)
 * Fonctionnalités : Vue d'ensemble, liste filtrée, marquage payé, notes admin, scheduler, création manuelle
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Paper, Card, CardContent, Grid, Typography, Chip, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TableSortLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Tabs, Tab,
  CircularProgress, Snackbar, Alert, Tooltip, Divider, Avatar,
  FormControlLabel, Switch, LinearProgress, Stack, Collapse, Autocomplete,
  Popover, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  List as ListIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  RestartAlt as RestartAltIcon,
  FlashOn as ExecuteIcon,
  NoteAdd as NoteAddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Build as BuildIcon,
  FilterList as FilterListIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import apiClient from '../../utils/apiClient';
import AdminCommissionsTab from '../../components/admin/AdminCommissionsTab';
import dayjs from 'dayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

/* Styled components */

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] }
}));

const StatusChip = styled(Chip)(({ theme, statuskey }) => {
  const colors = {
    pending:           { bg: theme.palette.info.light,    color: theme.palette.info.dark },
    processing:        { bg: theme.palette.primary.light, color: theme.palette.primary.dark },
    completed:         { bg: theme.palette.success.light, color: theme.palette.success.dark },
    overdue:           { bg: theme.palette.error.light,   color: theme.palette.error.dark },
    cancelled:         { bg: theme.palette.grey[300],     color: theme.palette.grey[700] },
    link_sent:         { bg: theme.palette.secondary.light, color: theme.palette.secondary.dark },
    payment_initiated: { bg: theme.palette.warning.light, color: theme.palette.warning.dark },
    expired:           { bg: theme.palette.grey[400],     color: theme.palette.grey[800] }
  };
  const c = colors[statuskey] || colors.cancelled;
  return { backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: '0.72rem' };
});

const FilterBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2
}));

const SchedulerCard = styled(Paper)(({ theme, running }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  borderLeft: `4px solid ${running === 'true' ? theme.palette.success.main : theme.palette.grey[400]}`
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const STATUS_LABELS = {
  pending:           'En attente',
  processing:        'En traitement',
  completed:         'Complété',
  overdue:           'En retard',
  cancelled:         'Annulé',
  link_sent:         'Lien envoyé',
  payment_initiated: 'Paiement initié',
  expired:           'Expiré'
};

const PAYIN_TYPE_LABELS = {
  commission: 'Commission',
  penalty:    'Pénalité',
  late_fee:   'Frais retard',
  adjustment: 'Ajustement'
};

// ─── DateRangePicker ──────────────────────────────────────────────────────────

function RangeDay({ day, outsideCurrentMonth, rangeStart, rangeEnd, onSelectDay, ...other }) {
  const isStart = rangeStart && day.isSame(rangeStart, 'day');
  const isEnd   = rangeEnd   && day.isSame(rangeEnd,   'day');
  const inRange = rangeStart && rangeEnd
    && day.isAfter(rangeStart, 'day') && day.isBefore(rangeEnd, 'day');

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      sx={{
        ...(inRange && !isStart && !isEnd && {
          borderRadius: 0,
          bgcolor: 'primary.50',
          '&:hover': { bgcolor: 'primary.100' },
        }),
        ...(isStart && {
          borderRadius: isEnd ? '50%' : '50% 0 0 50%',
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover, &:focus': { bgcolor: 'primary.dark' },
        }),
        ...(!isStart && isEnd && {
          borderRadius: '0 50% 50% 0',
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover, &:focus': { bgcolor: 'primary.dark' },
        }),
      }}
      onClick={() => !outsideCurrentMonth && onSelectDay(day)}
    />
  );
}

function DateRangePickerField({ value, onChange }) {
  const [anchor,    setAnchor]    = useState(null);
  const [step,      setStep]      = useState('start');
  const [tempStart, setTempStart] = useState(value.start ? dayjs(value.start) : null);
  const [tempEnd,   setTempEnd]   = useState(value.end   ? dayjs(value.end)   : null);

  useEffect(() => {
    setTempStart(value.start ? dayjs(value.start) : null);
    setTempEnd(value.end     ? dayjs(value.end)   : null);
  }, [value.start, value.end]);

  const displayText = value.start && value.end
    ? `${dayjs(value.start).format('DD/MM/YYYY')} → ${dayjs(value.end).format('DD/MM/YYYY')}`
    : value.start
    ? `Depuis ${dayjs(value.start).format('DD/MM/YYYY')}`
    : 'Toutes les dates';

  const handleDayClick = (date) => {
    if (step === 'start') {
      setTempStart(date);
      setTempEnd(null);
      setStep('end');
    } else {
      const s = date.isBefore(tempStart) ? date      : tempStart;
      const e = date.isBefore(tempStart) ? tempStart : date;
      setTempStart(s);
      setTempEnd(e);
      setStep('start');
      onChange({ start: s.format('YYYY-MM-DD'), end: e.format('YYYY-MM-DD') });
      setAnchor(null);
    }
  };

  const handleClear = (ev) => {
    ev?.stopPropagation();
    setTempStart(null);
    setTempEnd(null);
    setStep('start');
    onChange({ start: '', end: '' });
    setAnchor(null);
  };

  return (
    <>
      <Box
        onClick={e => setAnchor(e.currentTarget)}
        sx={{
          border: '1px solid',
          borderColor: anchor ? 'primary.main' : 'rgba(0,0,0,0.23)',
          borderRadius: 1, px: 1.5, py: 0.6,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
          '&:hover': { borderColor: 'text.primary' },
          minHeight: 40,
        }}
      >
        <CalendarIcon sx={{ fontSize: 18, color: anchor ? 'primary.main' : 'text.secondary' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', lineHeight: 1, mb: 0.3, fontSize: '0.68rem' }}
          >
            Période
          </Typography>
          <Typography variant="body2" color={value.start ? 'text.primary' : 'text.secondary'}>
            {displayText}
          </Typography>
        </Box>
        {(value.start || value.end) && (
          <IconButton size="small" sx={{ p: 0.3 }} onClick={handleClear}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setStep('start'); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 0.5, borderRadius: 2 } }}
      >
        <Box sx={{ p: 1, minWidth: 300 }}>
          <Typography
            variant="caption" fontWeight={600}
            color={step === 'start' ? 'primary.main' : 'secondary.main'}
            sx={{ px: 1, display: 'block', mb: 0.5 }}
          >
            {step === 'start'
              ? '① Sélectionnez la date de début'
              : `② Sélectionnez la date de fin  (début : ${tempStart?.format('DD/MM/YYYY')})`}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={tempStart || null}
              slots={{ day: RangeDay }}
              slotProps={{ day: { rangeStart: tempStart, rangeEnd: tempEnd, onSelectDay: handleDayClick } }}
              onChange={() => {}}
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
            <Button size="small" onClick={handleClear} startIcon={<ClearIcon />}>Effacer</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminPayinsPage = () => {
  const location = useLocation();
  const [tabValue, setTabValue]             = useState(0);
  const [loading, setLoading]               = useState(false);
  const [refreshing, setRefreshing]         = useState(false);
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'info' });
  const [showFilters, setShowFilters]       = useState(false);

  // Overview
  const [overview, setOverview] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [ownerSearchInput, setOwnerSearchInput] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Payins list
  const [payins, setPayins]           = useState([]);
  const [totalPayins, setTotalPayins] = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    status: '', ownerId: '', dateRange: { start: '', end: '' },
    minAmount: '', maxAmount: '', search: '', overdue_only: false,
    sortBy: 'due_date', sortOrder: 'DESC'
  });

  // Scheduler
  const [schedulerStatus, setSchedulerStatus]   = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);

  // Dialogs
  const [detailDialog,   setDetailDialog]   = useState({ open: false, payin: null });
  const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, payin: null, loading: false });
  const [notesDialog,    setNotesDialog]    = useState({ open: false, payin: null, loading: false });
  const [confirmDialog,  setConfirmDialog]  = useState({ open: false, title: '', message: '', onConfirm: null });

  // Mark paid form
  const [markPaidForm, setMarkPaidForm] = useState({
    payment_method: 'manual', payment_reference: '', admin_notes: '', override_amount: ''
  });

  // Notes form
  const [notesForm, setNotesForm] = useState({ notes: '' });

  // Create payin form
  const [createForm, setCreateForm] = useState({
    owner_id: '', reservation_id: '', commission_amount: '', taxes: '0',
    currency: 'EUR', payin_type: 'commission', due_date: '', notes: '', cash_amount_received: ''
  });
  const [createLoading, setCreateLoading] = useState(false);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount == null) return '—';
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR' })
        .format(parseFloat(amount) || 0);
    } catch {
      return `${parseFloat(amount) || 0} ${currency}`;
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatEcheance = (due_date) => {
    if (!due_date) return '—';
    const d = new Date(due_date);
    const echeance = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return echeance.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const getPaymentDate = (payin) => {
    if (!payin || payin.status !== 'completed') return null;
    return payin.paid_date || null;
  };

  const getEcheanceDate = (due_date) => {
    if (!due_date) return null;
    const d = new Date(due_date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 1);
  };

  const getDaysOverdueFromEcheance = (payin) => {
    const echeance = getEcheanceDate(payin.due_date);
    if (!echeance) return 0;
    const referenceDate = getPaymentDate(payin) ? new Date(getPaymentDate(payin)) : new Date();
    const diffTime = referenceDate - echeance;
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => showSnackbar('Copié !', 'success'));
  };

  const getOwnerOptionLabel = (owner) => {
    if (!owner) return '';
    const name = owner.name || owner.id || 'Utilisateur';
    return owner.email ? `${name} (${owner.email})` : name;
  };

  const formatMetadataValue = (value) => {
    if (value == null) return '—';
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (value instanceof Date) return formatDateTime(value);
    if (Array.isArray(value)) return value.length ? `${value.length} élément(s)` : '[]';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderMetadataSection = (title, entries) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      <Grid container spacing={1}>
        {entries.map(({ label, value, mono = false, fullWidth = false }) => (
          <Grid item xs={12} sm={fullWidth ? 12 : 6} key={label}>
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {label}
              </Typography>
              <Typography
                variant="body2"
                fontFamily={mono ? 'monospace' : 'inherit'}
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {formatMetadataValue(value)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // ─── API calls ─────────────────────────────────────────────────────────────

  const loadOverview = async () => {
    try {
      const res = await apiClient.get('/admin/financial/payins/overview');
      setOverview(res.data?.data || res.data);
    } catch (err) {
      console.error('loadOverview error', err);
    }
  };

  const loadPayins = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status)      params.set('status',      filters.status);
      if (filters.ownerId)     params.set('ownerId',     filters.ownerId);
      if (filters.dateRange.start)   params.set('startDate',   filters.dateRange.start);
      if (filters.dateRange.end)     params.set('endDate',     filters.dateRange.end);
      if (filters.minAmount)   params.set('minAmount',   filters.minAmount);
      if (filters.maxAmount)   params.set('maxAmount',   filters.maxAmount);
      if (filters.search)      params.set('search',      filters.search);
      if (filters.overdue_only) params.set('overdue_only', 'true');
      params.set('sortBy',    filters.sortBy);
      params.set('sortOrder', filters.sortOrder);
      params.set('limit',     rowsPerPage);
      params.set('offset',    page * rowsPerPage);
      params.set('include_owner',       'true');
      params.set('include_reservation', 'true');

      const res  = await apiClient.get(`/admin/financial/payins?${params}`);
      const data = res.data?.data || res.data;

      if (data?.payins) {
        setPayins(data.payins);
        setTotalPayins(data.total || data.payins.length);
      } else if (Array.isArray(data)) {
        setPayins(data);
        setTotalPayins(data.length);
      } else if (Array.isArray(data?.data)) {
        setPayins(data.data);
        setTotalPayins(data.total || data.data.length);
      }
    } catch (err) {
      console.error('loadPayins error', err);
      showSnackbar('Erreur lors du chargement des payins', 'error');
    }
  }, [filters, page, rowsPerPage]);

  const loadSchedulerStatus = async () => {
    try {
      const res = await apiClient.get('/admin/payin-scheduler/status');
      setSchedulerStatus(res.data?.data || res.data);
    } catch (err) {
      console.error('loadSchedulerStatus error', err);
    }
  };

  const loadOwners = async (query = '') => {
    setLoadingOwners(true);
    try {
      let ownersList = [];
      if (query && query.trim().length >= 2) {
        // apiClient already unwraps response.data → res is the JSON body directly
        const res = await apiClient.get(`/admin/users/search?query=${encodeURIComponent(query.trim())}`);
        ownersList = res?.users || [];
      } else {
        const res = await apiClient.get('/admin/users');
        ownersList = res?.users || [];
      }
      setOwners(ownersList);
    } catch (err) {
      console.error('loadOwners error', err);
    } finally {
      setLoadingOwners(false);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadOverview(), loadPayins(), loadSchedulerStatus()]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  useEffect(() => { loadAllData(); }, []); // eslint-disable-line
  useEffect(() => { loadPayins();  }, [loadPayins]);
  useEffect(() => { loadOwners(''); }, []); // Charger la liste des propriétaires au démarrage
  useEffect(() => {
    if (typeof location.state?.initialTab === 'number') {
      setTabValue(location.state.initialTab);
    }
  }, [location.state]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const openMarkPaid = (payin) => {
    setMarkPaidForm({
      payment_method:   'manual',
      payment_reference: payin.payment_reference || `MANUAL_${Date.now()}`,
      admin_notes:      '',
      override_amount:  ''
    });
    setMarkPaidDialog({ open: true, payin, loading: false });
  };

  const handleMarkPaid = async () => {
    const { payin } = markPaidDialog;
    if (!payin || !markPaidForm.payment_reference.trim()) {
      showSnackbar('La référence de paiement est obligatoire', 'warning');
      return;
    }
    setMarkPaidDialog(d => ({ ...d, loading: true }));
    try {
      const body = {
        payment_method:    markPaidForm.payment_method,
        payment_reference: markPaidForm.payment_reference.trim(),
        ...(markPaidForm.admin_notes     && { admin_notes:     markPaidForm.admin_notes }),
        ...(markPaidForm.override_amount && { override_amount: parseFloat(markPaidForm.override_amount) })
      };
      await apiClient.put(`/admin/financial/payins/${payin.id}/mark-paid`, body);
      showSnackbar(`Payin ${payin.payment_reference} marqué comme payé`, 'success');
      setMarkPaidDialog({ open: false, payin: null, loading: false });
      await Promise.all([loadPayins(), loadOverview()]);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du marquage', 'error');
      setMarkPaidDialog(d => ({ ...d, loading: false }));
    }
  };

  const openNotes = (payin) => {
    setNotesForm({ notes: payin.notes || '' });
    setNotesDialog({ open: true, payin, loading: false });
  };

  const handleAddNotes = async () => {
    const { payin } = notesDialog;
    if (!payin || !notesForm.notes.trim()) {
      showSnackbar('La note ne peut pas être vide', 'warning');
      return;
    }
    setNotesDialog(d => ({ ...d, loading: true }));
    try {
      await apiClient.put(`/admin/financial/payins/${payin.id}/notes`, { notes: notesForm.notes.trim() });
      showSnackbar('Note enregistrée', 'success');
      setNotesDialog({ open: false, payin: null, loading: false });
      await loadPayins();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de l\'ajout de note', 'error');
      setNotesDialog(d => ({ ...d, loading: false }));
    }
  };

  const handleProcessOverdue = () => {
    setConfirmDialog({
      open: true,
      title: 'Traiter les payins overdue',
      message: 'Cela va déclencher la création de factures de rattrapage pour tous les payins en retard sans facture. Continuer ?',
      onConfirm: async () => {
        try {
          const res = await apiClient.post('/admin/financial/payins/process-overdue');
          const processed = res.data?.data?.processed ?? res.data?.processed ?? 0;
          showSnackbar(`${processed} payin(s) overdue traité(s)`, 'success');
          await Promise.all([loadPayins(), loadOverview()]);
        } catch (err) {
          showSnackbar(err.response?.data?.message || 'Erreur lors du traitement', 'error');
        }
      }
    });
  };

  const handleCreatePayin = async () => {
    if (!createForm.owner_id.trim() || !createForm.commission_amount || !createForm.due_date) {
      showSnackbar('Owner ID, montant commission et date d\'échéance sont obligatoires', 'warning');
      return;
    }
    setCreateLoading(true);
    try {
      const body = {
        owner_id:          createForm.owner_id.trim(),
        commission_amount: parseFloat(createForm.commission_amount),
        taxes:             parseFloat(createForm.taxes || 0),
        currency:          createForm.currency,
        payin_type:        createForm.payin_type,
        due_date:          createForm.due_date,
        ...(createForm.reservation_id       && { reservation_id:       createForm.reservation_id.trim() }),
        ...(createForm.notes                && { notes:                createForm.notes }),
        ...(createForm.cash_amount_received && { cash_amount_received: parseFloat(createForm.cash_amount_received) })
      };
      await apiClient.post('/admin/financial/payins/manual', body);
      showSnackbar('Payin créé avec succès', 'success');
      setCreateForm({
        owner_id: '', reservation_id: '', commission_amount: '', taxes: '0',
        currency: 'EUR', payin_type: 'commission', due_date: '', notes: '', cash_amount_received: ''
      });
      await Promise.all([loadPayins(), loadOverview()]);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de la création', 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSchedulerAction = async (action, body = {}) => {
    setSchedulerLoading(true);
    try {
      await apiClient.post(`/admin/payin-scheduler/${action}`, body);
      const msgs = { start: 'Scheduler démarré', stop: 'Scheduler arrêté', restart: 'Scheduler redémarré', execute: 'Exécution manuelle lancée' };
      showSnackbar(msgs[action] || 'Action effectuée', 'success');
      await loadSchedulerStatus();
    } catch (err) {
      showSnackbar(err.response?.data?.message || `Erreur: ${action}`, 'error');
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleSort = (field) => {
    setFilters(f => ({
      ...f,
      sortBy:    field,
      sortOrder: f.sortBy === field && f.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
    setPage(0);
  };

  const resetFilters = () => {
    setFilters({
      status: '', ownerId: '', dateRange: { start: '', end: '' },
      minAmount: '', maxAmount: '', search: '', overdue_only: false,
      sortBy: 'due_date', sortOrder: 'DESC'
    });
    setSelectedOwner(null);
    setOwnerSearchInput('');
    setPage(0);
  };

  const activeFilterCount = [
    filters.status, filters.ownerId, filters.dateRange.start, filters.dateRange.end,
    filters.minAmount, filters.maxAmount, filters.search, filters.overdue_only
  ].filter(Boolean).length;

  // ─── Overview Tab ──────────────────────────────────────────────────────────

  const renderOverviewTab = () => {
    const s = overview?.summary   || {};
    const f = overview?.financial || {};
    const p = overview?.performance || {};
    const pbs = p.payins_by_status || {};

    return (
      <Box>
        {/* KPI Cards row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Total */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 1.5, width: 40, height: 40 }}>
                    <WalletIcon sx={{ color: 'primary.dark', fontSize: 20 }} />
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">Total payins</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">{s.total_payins ?? '—'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Dû : {formatCurrency(f.total_amount_due)}
                </Typography>
              </CardContent>
            </StatsCard>
          </Grid>

          {/* Pending */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'info.light', mr: 1.5, width: 40, height: 40 }}>
                    <CalendarIcon sx={{ color: 'info.dark', fontSize: 20 }} />
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">En attente</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {s.pending_payins ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatCurrency(pbs.pending?.amount_converted)}
                </Typography>
              </CardContent>
            </StatsCard>
          </Grid>

          {/* Overdue */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'error.light', mr: 1.5, width: 40, height: 40 }}>
                    <WarningIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">En retard</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {s.overdue_payins ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatCurrency(f.overdue_amount)} — {((s.overdue_rate || 0) * 100).toFixed(1)}%
                </Typography>
              </CardContent>
            </StatsCard>
          </Grid>

          {/* Completed */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'success.light', mr: 1.5, width: 40, height: 40 }}>
                    <CheckCircleIcon sx={{ color: 'success.dark', fontSize: 20 }} />
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">Complétés</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {s.completed_payins ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatCurrency(f.collected_amount)} collecté
                </Typography>
              </CardContent>
            </StatsCard>
          </Grid>
        </Grid>

        {/* Métriques secondaires */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Taux de collecte
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {((f.collection_rate || 0) * 100).toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">des montants dus</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(f.collection_rate || 0) * 100}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Taux de complétion : {((s.completion_rate || 0) * 100).toFixed(1)}%
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Retard moyen de paiement
              </Typography>
              <Typography variant="h5" fontWeight="bold" color={p.average_payment_delay > 7 ? 'error.main' : 'text.primary'}>
                {p.average_payment_delay ?? '—'} jours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Entre émission et paiement effectif
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Actions rapides
              </Typography>
              <Stack spacing={1}>
                <Button
                  variant="contained" color="warning" size="small"
                  startIcon={<ExecuteIcon />}
                  onClick={handleProcessOverdue}
                  fullWidth
                >
                  Traiter payins overdue
                </Button>
                <Button
                  variant="outlined" size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  fullWidth
                >
                  Rafraîchir les données
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Tendances mensuelles */}
        {p.monthly_trends?.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
              Tendances mensuelles
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {p.monthly_trends.slice(-12).map((t) => (
                <Chip
                  key={t.month}
                  label={`${t.month} : ${t.count}`}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  // ─── Payins List Tab ───────────────────────────────────────────────────────

  const renderPayinsTab = () => (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Rechercher (référence, owner…)"
          value={filters.search}
          onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} /> }}
          sx={{ flexGrow: 1, minWidth: 220 }}
        />
        <Tooltip title="Filtres avancés">
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(v => !v)}
          >
            Filtres {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
        </Tooltip>
        {activeFilterCount > 0 && (
          <Tooltip title="Réinitialiser les filtres">
            <IconButton size="small" onClick={resetFilters}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button
          variant="outlined" size="small"
          startIcon={<ExecuteIcon />}
          color="warning"
          onClick={handleProcessOverdue}
        >
          Traiter overdue
        </Button>
        <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Collapse filter bar */}
      <Collapse in={showFilters}>
        <FilterBar elevation={1} sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  label="Statut"
                  value={filters.status}
                  onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <MenuItem key={v} value={v}>{l}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={owners}
                filterOptions={(options) => options}
                getOptionLabel={getOwnerOptionLabel}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={selectedOwner}
                inputValue={ownerSearchInput}
                onChange={(_, value) => {
                  setSelectedOwner(value || null);
                  setOwnerSearchInput(value ? getOwnerOptionLabel(value) : '');
                  setFilters(f => ({ ...f, ownerId: value?.id || '' }));
                  setPage(0);
                }}
                onInputChange={(_, value, reason) => {
                  setOwnerSearchInput(value);

                  if (reason === 'clear') {
                    setSelectedOwner(null);
                    setFilters(f => ({ ...f, ownerId: '' }));
                    setPage(0);
                    return;
                  }

                  if (reason !== 'input') {
                    return;
                  }

                  if (value.trim().length >= 2) {
                    loadOwners(value.trim());
                  }
                }}
                loading={loadingOwners}
                noOptionsText="Saisir un nom ou email"
                renderInput={p => (
                  <TextField {...p} label="Propriétaire" placeholder="Chercher par nom/email" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateRangePickerField
                value={filters.dateRange}
                onChange={range => { setFilters(f => ({ ...f, dateRange: range })); setPage(0); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" label="Montant min (€)"
                type="number" inputProps={{ min: 0 }}
                value={filters.minAmount}
                onChange={e => { setFilters(f => ({ ...f, minAmount: e.target.value })); setPage(0); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" label="Montant max (€)"
                type="number" inputProps={{ min: 0 }}
                value={filters.maxAmount}
                onChange={e => { setFilters(f => ({ ...f, maxAmount: e.target.value })); setPage(0); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={filters.overdue_only}
                    onChange={e => { setFilters(f => ({ ...f, overdue_only: e.target.checked })); setPage(0); }}
                  />
                }
                label="Overdue uniquement"
              />
            </Grid>
          </Grid>
        </FilterBar>
      </Collapse>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>
                <TableSortLabel
                  active={filters.sortBy === 'created_at'}
                  direction={filters.sortBy === 'created_at' ? filters.sortOrder.toLowerCase() : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Référence
                </TableSortLabel>
              </TableCell>
              <TableCell>Propriétaire</TableCell>
              <TableCell>
                <TableSortLabel
                  active={filters.sortBy === 'commission_amount'}
                  direction={filters.sortBy === 'commission_amount' ? filters.sortOrder.toLowerCase() : 'asc'}
                  onClick={() => handleSort('commission_amount')}
                >
                  Montant
                </TableSortLabel>
              </TableCell>
              <TableCell>Taxes</TableCell>
              <TableCell>Total (€)</TableCell>
              <TableCell>Taux</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>
                <TableSortLabel
                  active={filters.sortBy === 'due_date'}
                  direction={filters.sortBy === 'due_date' ? filters.sortOrder.toLowerCase() : 'asc'}
                  onClick={() => handleSort('due_date')}
                >
                  Échéance
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={filters.sortBy === 'status'}
                  direction={filters.sortBy === 'status' ? filters.sortOrder.toLowerCase() : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell>Retard</TableCell>
              <TableCell>Facture</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  {loading ? <CircularProgress size={24} /> : 'Aucun payin trouvé'}
                </TableCell>
              </TableRow>
            ) : (
              payins.map((payin) => {
                const total = (parseFloat(payin.commission_amount) || 0) + (parseFloat(payin.taxes) || 0);
                const owner = payin.owner || {};
                const overdueDays = getDaysOverdueFromEcheance(payin);
                const isOverdue = payin.status === 'overdue' || overdueDays > 0;

                return (
                  <TableRow
                    key={payin.id}
                    hover
                    sx={{ bgcolor: isOverdue ? 'error.50' : 'inherit', opacity: payin.status === 'cancelled' ? 0.6 : 1 }}
                  >
                    <TableCell sx={{ maxWidth: 90 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {payin.payment_reference?.slice(0, 12) || payin.id?.slice(0, 8)}…
                        </Typography>
                        <Tooltip title="Copier la référence">
                          <IconButton size="small" onClick={() => copyToClipboard(payin.payment_reference || payin.id)}
                            sx={{ p: 0.2 }}>
                            <CopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatDate(payin.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 130 }}>
                            {owner.name || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 130, display: 'block' }}>
                            {owner.email || payin.owner_id?.slice(0, 12)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(payin.commission_amount, payin.original_currency || payin.currency)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(payin.taxes, payin.original_currency || payin.currency)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        {formatCurrency(
                          ((parseFloat(payin.commission_amount) || 0) + (parseFloat(payin.taxes) || 0)) / (parseFloat(payin.exchange_rate) || 1),
                          'EUR'
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box>
                        {payin.exchange_rate != null && parseFloat(payin.exchange_rate) !== 1 && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Change: {parseFloat(payin.exchange_rate).toFixed(4)}
                          </Typography>
                        )}
                        {payin.commission_rate != null && (
                          <Typography variant="caption" display="block" color="info.main">
                            Comm: {(parseFloat(payin.commission_rate) * 100).toFixed(2)}%
                          </Typography>
                        )}
                        {parseFloat(payin.taxes) > 0 && parseFloat(payin.commission_amount) > 0 && (
                          <Typography variant="caption" display="block" color="warning.main">
                            TVA: {((parseFloat(payin.taxes) / parseFloat(payin.commission_amount)) * 100).toFixed(2)}%
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={PAYIN_TYPE_LABELS[payin.payin_type] || payin.payin_type}
                        size="small" variant="outlined"
                        sx={{ fontSize: '0.68rem' }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{formatEcheance(payin.due_date)}</Typography>
                      {payin.status === 'completed' && getPaymentDate(payin) && (
                        <Typography variant="caption" color="success.main">
                          Payé : {formatDate(getPaymentDate(payin))}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <StatusChip
                        label={STATUS_LABELS[payin.status] || payin.status}
                        statuskey={payin.status}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      {overdueDays > 0 ? (
                        <Typography variant="body2" color="error.main" fontWeight={600}>
                          {overdueDays}j
                        </Typography>
                      ) : '—'}
                    </TableCell>

                    <TableCell>
                      {payin.invoice_id ? (
                        <Chip label="Facturé" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Non facturé" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Détails">
                          <IconButton size="small" onClick={() => setDetailDialog({ open: true, payin })}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {['pending', 'overdue', 'link_sent', 'payment_initiated'].includes(payin.status) && (
                          <Tooltip title="Marquer payé">
                            <IconButton size="small" color="success" onClick={() => openMarkPaid(payin)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Ajouter une note">
                          <IconButton size="small" color="primary" onClick={() => openNotes(payin)}>
                            <NoteAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {payin.stripe_payment_link_url && (
                          <Tooltip title="Ouvrir le lien Stripe">
                            <IconButton size="small" onClick={() => window.open(payin.stripe_payment_link_url, '_blank')}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalPayins}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, v) => setPage(v)}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        labelRowsPerPage="Par page :"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
      />
    </Box>
  );

  // ─── Scheduler Tab ─────────────────────────────────────────────────────────

  const renderSchedulerTab = () => {
    const scheduler = schedulerStatus?.scheduler || schedulerStatus || {};
    const pendingStats = schedulerStatus?.pendingPayins || schedulerStatus?.pendingStats || {};

    const isRunning = scheduler?.isRunning === true || scheduler?.status === 'active' || scheduler?.status === 'running';
    const rawPending = pendingStats?.count ?? schedulerStatus?.pending_count ?? 0;
    const pending = typeof rawPending === 'object' ? (rawPending?.count ?? 0) : (parseInt(rawPending, 10) || 0);
    const rawAmount = pendingStats?.totalAmount ?? schedulerStatus?.pendingAmount ?? schedulerStatus?.pending_amount ?? 0;
    const amount = typeof rawAmount === 'object' ? parseFloat(rawAmount?.total ?? 0) : (parseFloat(rawAmount) || 0);

    return (
      <Grid container spacing={3}>
        {/* Status card */}
        <Grid item xs={12} md={5}>
          <SchedulerCard elevation={2} running={String(isRunning)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: isRunning ? 'success.light' : 'grey.200', width: 48, height: 48 }}>
                <CircleIcon sx={{ color: isRunning ? 'success.dark' : 'grey.500', fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">Scheduler Payins</Typography>
                <Chip
                  label={isRunning ? 'En cours' : 'Arrêté'}
                  color={isRunning ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Payins en attente</Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">{pending}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Montant total (€)</Typography>
                <Typography variant="h5" fontWeight="bold">{formatCurrency(amount)}</Typography>
              </Grid>
              {scheduler?.lastRun && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Dernière exécution</Typography>
                  <Typography variant="body2">{formatDateTime(scheduler.lastRun)}</Typography>
                </Grid>
              )}
              {scheduler?.nextRun && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Prochaine exécution</Typography>
                  <Typography variant="body2">{formatDateTime(scheduler.nextRun)}</Typography>
                </Grid>
              )}
            </Grid>

            {schedulerStatus && Object.keys(schedulerStatus).length > 0 && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {JSON.stringify(
                    Object.fromEntries(
                      Object.entries(schedulerStatus).filter(([k]) =>
                        !['isRunning', 'status', 'pendingPayins', 'pendingAmount', 'lastRun', 'nextRun'].includes(k)
                      )
                    ),
                    null, 2
                  )}
                </Typography>
              </Box>
            )}
          </SchedulerCard>
        </Grid>

        {/* Controls */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <BuildIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
              Contrôles du scheduler
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth variant="contained" color="success" size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleSchedulerAction('start')}
                  disabled={schedulerLoading || isRunning}
                  sx={{ py: 1.5, borderRadius: 2 }}
                >
                  Démarrer
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth variant="contained" color="error" size="large"
                  startIcon={<StopIcon />}
                  onClick={() => handleSchedulerAction('stop')}
                  disabled={schedulerLoading || !isRunning}
                  sx={{ py: 1.5, borderRadius: 2 }}
                >
                  Arrêter
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth variant="outlined" color="warning" size="large"
                  startIcon={<RestartAltIcon />}
                  onClick={() => handleSchedulerAction('restart')}
                  disabled={schedulerLoading}
                  sx={{ py: 1.5, borderRadius: 2 }}
                >
                  Redémarrer
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth variant="contained" color="primary" size="large"
                  startIcon={<ExecuteIcon />}
                  onClick={() => handleSchedulerAction('execute')}
                  disabled={schedulerLoading}
                  sx={{ py: 1.5, borderRadius: 2 }}
                >
                  Exécuter maintenant
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth variant="outlined" size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadSchedulerStatus}
                  disabled={schedulerLoading}
                >
                  Rafraîchir le statut
                </Button>
              </Grid>
            </Grid>

            {schedulerLoading && <LinearProgress sx={{ mt: 2, borderRadius: 2 }} />}

            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="caption" color="info.dark">
                <strong>Exécuter maintenant</strong> traite immédiatement tous les payins éligibles (pending du mois précédent + overdue sans facture).
                Le scheduler normal s'exécute automatiquement selon la configuration cron.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // ─── Create Payin Tab ──────────────────────────────────────────────────────

  const renderCreateTab = () => (
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} md={8} lg={6}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.light', width: 44, height: 44 }}>
              <AddIcon sx={{ color: 'primary.dark' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">Créer un payin manuel</Typography>
              <Typography variant="caption" color="text.secondary">
                Création forcée d'une commission en dehors du cycle automatique
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                required fullWidth label="ID du propriétaire (owner_id)"
                placeholder="UUID du propriétaire"
                value={createForm.owner_id}
                onChange={e => setCreateForm(f => ({ ...f, owner_id: e.target.value }))}
                helperText="UUID du compte propriétaire concerné"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="ID de réservation (optionnel)"
                placeholder="UUID de la réservation liée"
                value={createForm.reservation_id}
                onChange={e => setCreateForm(f => ({ ...f, reservation_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required fullWidth label="Montant commission (€)"
                type="number" inputProps={{ min: 0, step: 0.01 }}
                value={createForm.commission_amount}
                onChange={e => setCreateForm(f => ({ ...f, commission_amount: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="TVA / Taxes (€)"
                type="number" inputProps={{ min: 0, step: 0.01 }}
                value={createForm.taxes}
                onChange={e => setCreateForm(f => ({ ...f, taxes: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Type de payin</InputLabel>
                <Select
                  label="Type de payin"
                  value={createForm.payin_type}
                  onChange={e => setCreateForm(f => ({ ...f, payin_type: e.target.value }))}
                >
                  {Object.entries(PAYIN_TYPE_LABELS).map(([v, l]) => (
                    <MenuItem key={v} value={v}>{l}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Devise</InputLabel>
                <Select
                  label="Devise"
                  value={createForm.currency}
                  onChange={e => setCreateForm(f => ({ ...f, currency: e.target.value }))}
                >
                  {['EUR', 'USD', 'GBP', 'MAD', 'TND'].map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required fullWidth label="Date d'échéance" type="date"
                InputLabelProps={{ shrink: true }}
                value={createForm.due_date}
                onChange={e => setCreateForm(f => ({ ...f, due_date: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Montant espèces reçu (optionnel)" type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={createForm.cash_amount_received}
                onChange={e => setCreateForm(f => ({ ...f, cash_amount_received: e.target.value }))}
                helperText="Montant cash perçu du locataire"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Notes admin (optionnel)" multiline rows={3}
                placeholder="Contexte, motif de création manuelle…"
                value={createForm.notes}
                onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
              />
            </Grid>

            {createForm.commission_amount && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
                  <Typography variant="body2" fontWeight={600} color="primary.dark">
                    Total facturé : {formatCurrency(
                      (parseFloat(createForm.commission_amount) || 0) + (parseFloat(createForm.taxes) || 0),
                      createForm.currency
                    )}
                  </Typography>
                  <Typography variant="caption" color="primary.main">
                    Commission {formatCurrency(parseFloat(createForm.commission_amount) || 0, createForm.currency)}
                    {parseFloat(createForm.taxes) > 0 && ` + TVA ${formatCurrency(parseFloat(createForm.taxes), createForm.currency)}`}
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                fullWidth variant="contained" color="primary" size="large"
                startIcon={createLoading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                onClick={handleCreatePayin}
                disabled={createLoading}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                {createLoading ? 'Création en cours…' : 'Créer le payin'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  // ─── Dialogs ───────────────────────────────────────────────────────────────

  const renderDetailDialog = () => {
    const payin = detailDialog.payin;
    if (!payin) return null;
    const owner = payin.owner || {};
    const total = (parseFloat(payin.commission_amount) || 0) + (parseFloat(payin.taxes) || 0);
    const meta  = payin.metadata || {};
    const adminActions = meta.admin_action ? [meta.admin_action] : [];
    const adminNotes   = meta.admin_notes_history || [];
    const metadataEntries = Object.entries(meta || {}).filter(([key]) => !['admin_action', 'admin_notes_history'].includes(key));

    return (
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, payin: null })}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'primary.light' }}>
              <WalletIcon sx={{ color: 'primary.dark', fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">Détails du payin</Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {payin.payment_reference || payin.id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* Identité */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>IDENTIFICATION</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">ID</Typography>
                  <Typography variant="body2" fontFamily="monospace">{payin.id}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Référence</Typography>
                  <Typography variant="body2" fontFamily="monospace">{payin.payment_reference || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{PAYIN_TYPE_LABELS[payin.payin_type] || payin.payin_type}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Propriétaire */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>PROPRIÉTAIRE</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Nom</Typography>
                  <Typography variant="body2">{owner.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{owner.email || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">ID propriétaire</Typography>
                  <Typography variant="body2" fontFamily="monospace">{payin.owner_id}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Financier */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>FINANCIER</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Commission</Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(payin.commission_amount, payin.original_currency || payin.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Taxes</Typography>
                  <Typography variant="body2">{formatCurrency(payin.taxes, payin.original_currency || payin.currency)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    {formatCurrency(total, payin.original_currency || payin.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Devise</Typography>
                  <Typography variant="body2">{payin.original_currency || payin.currency}</Typography>
                </Grid>
                {payin.exchange_rate && payin.exchange_rate !== 1 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Taux de change : 1 EUR = {payin.exchange_rate} {payin.original_currency || payin.currency}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Statut & dates */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>STATUT & DATES</Typography>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Statut</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip label={STATUS_LABELS[payin.status] || payin.status} statuskey={payin.status} size="small" />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Échéance</Typography>
                  <Typography variant="body2">{formatEcheance(payin.due_date)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Date paiement</Typography>
                  <Typography variant="body2">{formatDate(getPaymentDate(payin))}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Jours de retard</Typography>
                  <Typography variant="body2" color={getDaysOverdueFromEcheance(payin) > 0 ? 'error.main' : 'text.primary'}>
                    {getDaysOverdueFromEcheance(payin) > 0 ? `${getDaysOverdueFromEcheance(payin)} jours` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Créé le</Typography>
                  <Typography variant="body2">{formatDate(payin.created_at)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Méthode paiement</Typography>
                  <Typography variant="body2">{payin.payment_method || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Tentatives</Typography>
                  <Typography variant="body2">{payin.payment_attempts || 0}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Facture</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {payin.invoice_id ? payin.invoice_id.slice(0, 12) + '…' : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Notes */}
            {payin.notes && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>NOTE ADMIN</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">{payin.notes}</Typography>
                  </Paper>
                </Grid>
              </>
            )}

            {/* Audit admin actions */}
            {adminActions.length > 0 && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>ACTIONS ADMIN</Typography>
                  {adminActions.map((action, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: 'warning.50' }}>
                      <Typography variant="caption" color="text.secondary">
                        {action.timestamp ? formatDateTime(action.timestamp) : ''} — {action.admin_id || 'Admin'}
                      </Typography>
                      <Typography variant="body2">{action.action || JSON.stringify(action)}</Typography>
                    </Paper>
                  ))}
                </Grid>
              </>
            )}

            {/* Historique notes */}
            {adminNotes.length > 0 && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    HISTORIQUE DES NOTES ({adminNotes.length})
                  </Typography>
                  {adminNotes.map((note, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {note.timestamp ? formatDateTime(note.timestamp) : ''}
                      </Typography>
                      <Typography variant="body2">{note.note || note.notes || note.text || String(note)}</Typography>
                    </Paper>
                  ))}
                </Grid>
              </>
            )}

            {/* Stripe info */}
            {(payin.stripe_payment_link_id || payin.stripe_payment_intent_id) && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>STRIPE</Typography>
                  {payin.stripe_payment_link_id && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Payment Link ID : <span style={{ fontFamily: 'monospace' }}>{payin.stripe_payment_link_id}</span>
                    </Typography>
                  )}
                  {payin.stripe_payment_intent_id && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Payment Intent : <span style={{ fontFamily: 'monospace' }}>{payin.stripe_payment_intent_id}</span>
                    </Typography>
                  )}
                  {payin.stripe_payment_link_url && (
                    <Button
                      size="small" startIcon={<OpenInNewIcon />} sx={{ mt: 1 }}
                      onClick={() => window.open(payin.stripe_payment_link_url, '_blank')}
                    >
                      Ouvrir le lien de paiement
                    </Button>
                  )}
                </Grid>
              </>
            )}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Accordion
                elevation={0}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ScheduleIcon />}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Metadata</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Données techniques, audit et informations complémentaires
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {metadataEntries.length === 0 && !meta.admin_action && !adminNotes.length ? (
                    <Typography variant="body2" color="text.secondary">Aucune metadata disponible</Typography>
                  ) : (
                    <>
                      {meta.admin_action && renderMetadataSection('Action admin', [
                        { label: 'Type', value: meta.admin_action.action || '—' },
                        { label: 'Marqué payé par', value: meta.admin_action.marked_paid_by || '—', mono: true },
                        { label: 'Marqué payé le', value: meta.admin_action.marked_paid_at },
                        { label: 'Notes admin', value: meta.admin_action.admin_notes || '—', fullWidth: true },
                        { label: 'Montant original', value: meta.admin_action.original_amount ?? '—' },
                        { label: 'Montant override', value: meta.admin_action.override_amount ?? '—' }
                      ])}

                      {adminNotes.length > 0 && renderMetadataSection(`Historique des notes (${adminNotes.length})`, [
                        { label: 'Dernière note', value: adminNotes[adminNotes.length - 1]?.note || adminNotes[adminNotes.length - 1]?.notes || adminNotes[adminNotes.length - 1]?.text || '—', fullWidth: true },
                        { label: 'Dernier horodatage', value: adminNotes[adminNotes.length - 1]?.timestamp || '—' }
                      ])}

                      {metadataEntries.length > 0 && renderMetadataSection('Autres champs',
                        metadataEntries.map(([key, value]) => ({
                          label: key.replace(/_/g, ' '),
                          value,
                          mono: typeof value === 'string' && value.length > 24,
                          fullWidth: typeof value === 'object' || typeof value === 'string'
                        }))
                      )}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {['pending', 'overdue', 'link_sent', 'payment_initiated'].includes(payin.status) && (
            <Button color="success" startIcon={<CheckCircleIcon />}
              onClick={() => { setDetailDialog({ open: false, payin: null }); openMarkPaid(payin); }}>
              Marquer payé
            </Button>
          )}
          <Button onClick={() => { setDetailDialog({ open: false, payin: null }); openNotes(payin); }}
            startIcon={<NoteAddIcon />}>
            Ajouter note
          </Button>
          <Button onClick={() => setDetailDialog({ open: false, payin: null })}>Fermer</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderMarkPaidDialog = () => (
    <Dialog open={markPaidDialog.open}
      onClose={() => !markPaidDialog.loading && setMarkPaidDialog({ open: false, payin: null, loading: false })}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          Marquer comme payé
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {markPaidDialog.payin && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.50', borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Payin : {markPaidDialog.payin.payment_reference}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Montant : {formatCurrency(
                (parseFloat(markPaidDialog.payin.commission_amount) || 0) + (parseFloat(markPaidDialog.payin.taxes) || 0),
                markPaidDialog.payin.currency
              )}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Méthode de paiement</InputLabel>
              <Select
                label="Méthode de paiement"
                value={markPaidForm.payment_method}
                onChange={e => setMarkPaidForm(f => ({ ...f, payment_method: e.target.value }))}
              >
                <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
                <MenuItem value="card">Carte bancaire</MenuItem>
                <MenuItem value="wallet">Portefeuille</MenuItem>
                <MenuItem value="offset">Compensation</MenuItem>
                <MenuItem value="manual">Manuel</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              required fullWidth label="Référence de paiement"
              value={markPaidForm.payment_reference}
              onChange={e => setMarkPaidForm(f => ({ ...f, payment_reference: e.target.value }))}
              helperText="Référence unique identifiant ce paiement (virement, chèque, etc.)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Montant réel reçu (optionnel)"
              type="number" inputProps={{ min: 0, step: 0.01 }}
              value={markPaidForm.override_amount}
              onChange={e => setMarkPaidForm(f => ({ ...f, override_amount: e.target.value }))}
              helperText="Laisser vide pour utiliser le montant de la facture"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Note admin (optionnel)" multiline rows={2}
              placeholder="Commentaire visible dans l'audit trail…"
              value={markPaidForm.admin_notes}
              onChange={e => setMarkPaidForm(f => ({ ...f, admin_notes: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setMarkPaidDialog({ open: false, payin: null, loading: false })}
          disabled={markPaidDialog.loading}>
          Annuler
        </Button>
        <Button variant="contained" color="success"
          startIcon={markPaidDialog.loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          onClick={handleMarkPaid} disabled={markPaidDialog.loading}>
          Confirmer le paiement
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderNotesDialog = () => (
    <Dialog open={notesDialog.open}
      onClose={() => !notesDialog.loading && setNotesDialog({ open: false, payin: null, loading: false })}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteAddIcon color="primary" />
          Note admin
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {notesDialog.payin && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Payin : {notesDialog.payin.payment_reference || notesDialog.payin.id?.slice(0, 12)}
          </Typography>
        )}
        <TextField
          fullWidth required label="Note" multiline rows={4}
          placeholder="Saisir la note ou le commentaire admin…"
          value={notesForm.notes}
          onChange={e => setNotesForm({ notes: e.target.value })}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNotesDialog({ open: false, payin: null, loading: false })}
          disabled={notesDialog.loading}>
          Annuler
        </Button>
        <Button variant="contained"
          startIcon={notesDialog.loading ? <CircularProgress size={16} color="inherit" /> : <NoteAddIcon />}
          onClick={handleAddNotes} disabled={notesDialog.loading}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderConfirmDialog = () => (
    <Dialog open={confirmDialog.open}
      onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
      maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        {confirmDialog.title}
      </DialogTitle>
      <DialogContent>
        <Typography>{confirmDialog.message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}>
          Annuler
        </Button>
        <Button variant="contained" color="warning"
          onClick={() => {
            confirmDialog.onConfirm?.();
            setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          }}>
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <AttachMoneyIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">Gestion des Payins</Typography>
            <Typography variant="body2" color="text.secondary">
              Commissions dues par les propriétaires · Suivi, facturation et rattrapage
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" size="small"
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleRefresh} disabled={refreshing}
          >
            Rafraîchir
          </Button>
          <Button
            variant="contained" size="small"
            startIcon={<AddIcon />}
            onClick={() => setTabValue(4)}
          >
            Nouveau payin
          </Button>
        </Box>
      </Box>

      {/* Global loading bar */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', px: 1 }}
        >
          <Tab label="Vue d'ensemble" icon={<DashboardIcon />} iconPosition="start" />
          <Tab
            label={`Payins${totalPayins > 0 ? ` (${totalPayins})` : ''}`}
            icon={<ListIcon />} iconPosition="start"
          />
          <Tab label="Commissions" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="Scheduler" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Créer un payin" icon={<AddIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <TabPanel value={tabValue} index={0}>{renderOverviewTab()}</TabPanel>
          <TabPanel value={tabValue} index={1}>{renderPayinsTab()}</TabPanel>
          <TabPanel value={tabValue} index={2}><AdminCommissionsTab onNotify={showSnackbar} /></TabPanel>
          <TabPanel value={tabValue} index={3}>{renderSchedulerTab()}</TabPanel>
          <TabPanel value={tabValue} index={4}>{renderCreateTab()}</TabPanel>
        </Box>
      </Paper>

      {/* Dialogs */}
      {renderDetailDialog()}
      {renderMarkPaidDialog()}
      {renderNotesDialog()}
      {renderConfirmDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPayinsPage;
